import React, { useEffect, useState } from 'react';
import { Calendar, Download, FileText } from 'lucide-react';
import {
    fetchKkReconciliation,
    downloadKkReport,
    KkReconciliation,
    ReportQuality,
    ScopeCity
} from '../../lib/api';
import { formatDanishNumber } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { MONTH_NAMES } from './period';

interface KkSettlementReportProps {
    city: ScopeCity;
    year: number;
    month: number;
}

/**
 * Card for cities on the `kk-settlement` report engine (KBH launch plan §9 P8):
 * the two byte-faithful KK template files plus the three-way reconciliation.
 *
 * Rendered only for cities the signed-in account is scoped to. The endpoints
 * behind it additionally run `requireCityAccess('kbh')`, so an Aalborg viewer
 * gets a 403 here even if this card were forced onto their screen.
 */
const KkSettlementReport: React.FC<KkSettlementReportProps> = ({ city, year, month }) => {
    const { user } = useAuth();
    // The transactions file lists every parking with its license plate, so the
    // API refuses it to read-only insights accounts; don't offer the button.
    const isInsights = user?.role === 'insights';
    const [recon, setRecon] = useState<KkReconciliation | null>(null);
    const [sessionCount, setSessionCount] = useState(0);
    const [quality, setQuality] = useState<ReportQuality | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [downloading, setDownloading] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    // A new period invalidates the reconciliation on screen.
    useEffect(() => {
        setRecon(null);
        setQuality(null);
        setSessionCount(0);
        setError(null);
    }, [year, month]);

    const handleReconcile = async () => {
        setIsLoading(true);
        setError(null);
        setRecon(null);
        setQuality(null);
        try {
            const resp = await fetchKkReconciliation(year, month);
            setRecon(resp.reconciliation);
            setSessionCount(resp.sessionCount);
            setQuality(resp.quality);
        } catch (err: any) {
            setError(err.message || 'Failed to load KK reconciliation');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = async (kind: 'monthly-zone' | 'transactions') => {
        setDownloading(kind);
        setError(null);
        try {
            await downloadKkReport(kind, year, month);
        } catch (err: any) {
            setError(err.message || 'Failed to download KK report');
        } finally {
            setDownloading(null);
        }
    };

    return (
        <div className="bg-white border rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
                {city.settlementName} — månedlig afregning
            </h2>
            <p className="text-sm text-gray-600 mb-4">
                Genererer de to KK-filer (byte-tro mod skabelonerne) og afstemningen for{' '}
                <strong>{MONTH_NAMES[month - 1]} {year}</strong>. Sendes til{' '}
                <span className="font-mono">stabregnskab@kk.dk</span> senest d. 15.
            </p>
            <div className="flex flex-wrap items-center gap-3">
                <button
                    onClick={handleReconcile}
                    disabled={isLoading}
                    className="px-5 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                    <Calendar className="h-4 w-4" />
                    {isLoading ? 'Afstemmer…' : `Afstem ${MONTH_NAMES[month - 1]} ${year}`}
                </button>
                <button
                    onClick={() => handleDownload('monthly-zone')}
                    disabled={downloading !== null || quality?.readyForClose === false}
                    className="px-5 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                    <FileText className="h-4 w-4" />
                    Download månedsrapport
                </button>
                {!isInsights && (
                    <button
                        onClick={() => handleDownload('transactions')}
                        disabled={downloading !== null || quality?.readyForClose === false}
                        className="px-5 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                        <Download className="h-4 w-4" />
                        Download enkelttransaktioner
                    </button>
                )}
            </div>

            {error && <p className="text-sm text-red-600 mt-3">{error}</p>}

            {quality && !quality.readyForClose && (
                <div className="mt-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
                    <div className="font-semibold">Rapporten er blokeret af datakvalitetsfejl</div>
                    <div>
                        {quality.exceptions.length} fejl i {quality.candidateSessions} kandidatsessioner. Filerne kan først hentes, når de er løst.
                    </div>
                    <div className="mt-1 font-mono text-xs">
                        {Array.from(new Set(quality.exceptions.map(issue => issue.code))).join(', ')}
                    </div>
                </div>
            )}

            {recon && (
                <div className="mt-5 border-t pt-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                            <div className="text-gray-500">Transaktioner</div>
                            <div className="font-semibold text-gray-900">{recon.transactions}</div>
                        </div>
                        <div>
                            <div className="text-gray-500">Beløb til KK (ubeskåret)</div>
                            <div className="font-semibold text-gray-900">{formatDanishNumber(recon.amountToKK, 2)} DKK</div>
                        </div>
                        <div>
                            <div className="text-gray-500">Vores service fee (beholdes)</div>
                            <div className="font-semibold text-gray-900">{formatDanishNumber(recon.serviceFeeRetained, 2)} DKK</div>
                        </div>
                        <div>
                            <div className="text-gray-500">Registreret Nexi-opkrævning (netto)</div>
                            <div className="font-semibold text-gray-900">{formatDanishNumber(recon.nexiCapturedNet, 2)} DKK</div>
                        </div>
                    </div>
                    <div className={`mt-4 px-4 py-3 rounded-md text-sm font-medium ${recon.nexiMatches && quality?.readyForClose !== false ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                        {(recon.missingCapturedAmountCount || 0) > 0
                            ? `✗ MANGLENDE BETALINGSDATA — ${recon.missingCapturedAmountCount} opkrævning(er) mangler registreret beløb (${formatDanishNumber(recon.missingCapturedAmountTotal || 0, 2)} DKK). Ret data før overførsel.`
                            : quality?.readyForClose === false
                            ? '✗ RAPPORT BLOKERET — område-, registrerings- eller afregningsdata skal rettes før overførsel.'
                            : recon.nexiMatches
                            ? '✓ Afstemt — Nexi-opkrævning matcher parkering + service fee. Klar til overførsel.'
                            : '✗ AFVIGELSE — Nexi-opkrævning matcher ikke parkering + service fee. Undersøg før overførsel.'}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                        Overfør <strong>{formatDanishNumber(recon.amountToKK, 2)} DKK</strong> til Danske Bank reg. {recon.bankAccount.reg} konto {recon.bankAccount.konto}. ({sessionCount} KBH-sessioner i perioden.)
                    </p>
                </div>
            )}
        </div>
    );
};

export default KkSettlementReport;
